"use client";

import { blockedEmailError } from "@/lib/emailPolicy";
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  Building2,
  Check,
  CheckCircle2,
  ChevronDown,
  Cpu,
  Languages,
  Loader2,
  MessagesSquare,
  Plane,
  Send,
  Smile,
} from "lucide-react";
import {
  batchOptions,
  corporateProgrammes,
  interestedCourses,
  itCourseOptions,
  trainingModes,
  travelCourseOptions,
  travelProgrammes,
} from "@/lib/portal/enquiries";
import { submitEnquiryApi, type PublicEnquiryType } from "@/lib/enquiry";
import { PhoneInput, validatePhone } from "@/components/ui/PhoneInput";
import { cn } from "@/lib/utils";
import { readSession } from "@/lib/portal/session";
import { apiMyCourses } from "@/lib/portal/api";
import { useToast } from "@/components/portal/Toast";
import { isStaffSession, STAFF_ENQUIRY_NOTICE } from "@/components/layout/StaffEnquiryGuard";
import { LegalModalLink } from "@/components/legal/LegalModalLink";

export type EnquiryFormTab = "general" | "travel" | "personality" | "it" | "corporate" | "ielts";

/** Dynamic option list for the active form; falls back to the seeded statics. */
function listOpt(
  typeMeta: PublicEnquiryType | undefined,
  name: string,
  fallback: string[]
): string[] {
  const v = typeMeta?.options?.[name];
  return Array.isArray(v) && v.length > 0 ? v : fallback;
}

/** Locked batch: the configured `defaultBatch` if valid, else the weekend option, else the first option. */
function pickDefaultBatch(typeMeta: PublicEnquiryType | undefined, options: string[]): string {
  const configured = typeMeta?.options?.defaultBatch;
  if (typeof configured === "string" && options.includes(configured)) return configured;
  return options.find((o) => /weekend/i.test(o)) ?? options[0] ?? "";
}

const TABS: { id: EnquiryFormTab; label: string; blurb: string; icon: ReactNode }[] = [
  { id: "general", label: "General Enquiry", blurb: "Tell us the area you are interested in and we will point you to the right program.", icon: <MessagesSquare className="h-5 w-5" /> },
  { id: "travel", label: "Travel & Tourism", blurb: "IATA diploma and MVA certificate batches, with weekday and weekend options.", icon: <Plane className="h-5 w-5" /> },
  { id: "personality", label: "Personality Development", blurb: "Confidence, communication and grooming sessions for students and professionals.", icon: <Smile className="h-5 w-5" /> },
  { id: "it", label: "IT Courses", blurb: "Gen AI, DM & AI, AI & Cybersecurity and Claude Mastery programs.", icon: <Cpu className="h-5 w-5" /> },
  { id: "corporate", label: "Corporate Training", blurb: "Custom training for your teams, at MVA or at your organisation.", icon: <Building2 className="h-5 w-5" /> },
  { id: "ielts", label: "IELTS", blurb: "Academic and General IELTS coaching with mock tests.", icon: <Languages className="h-5 w-5" /> },
];

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** City: letters only, with spaces . ' - between words (e.g. "New Delhi", "St. John's"). No digits or symbols. */
const cityRe = /^[A-Za-z][A-Za-z\s.'’-]*$/;
const cleanCity = (v: string) => v.replace(/[^A-Za-z\s.'’-]/g, "");
/** "Other" in a dropdown / checklist opens a free-text field for the details. */
const isOther = (s: string) => /^other$/i.test(s.trim());

const autoProgramme: Partial<Record<EnquiryFormTab, string>> = {
  personality: "Personality Development",
  ielts: "IELTS",
};

export function EnquiryFormTabs({
  defaultTab = "general",
  scope = "all",
  defaultCourse,
  types,
}: {
  defaultTab?: EnquiryFormTab;
  /** Course picked on the way in (from a course page) - preselected in the programme field. */
  defaultCourse?: { title: string; category?: string };
  scope?: "all" | EnquiryFormTab;
  types?: PublicEnquiryType[];
}) {
  const toast = useToast();
  const [tab, setTab] = useState<EnquiryFormTab>(scope === "all" ? defaultTab : scope);
  const startTab = scope === "all" ? defaultTab : scope;
  const [values, setValues] = useState<Record<string, string>>((): Record<string, string> => {
    if (!defaultCourse || scope !== "all") return {};
    if (startTab === "travel" || startTab === "it") return { programme: defaultCourse.title };
    if (startTab === "general" && defaultCourse.category) return { interestedCourse: defaultCourse.category };
    return {};
  });
  const [programmes, setProgrammes] = useState<string[]>(() => (defaultCourse && scope === "all" && (startTab === "general" || startTab === "corporate") ? [defaultCourse.title] : []));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState("");
  const [reference, setReference] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done">("idle");

  const tabMeta = TABS.find((t) => t.id === tab) ?? TABS[0];
  const typeMeta = types?.find((t) => t.key === tab);
  const active = {
    label: typeMeta?.label ?? tabMeta.label,
    blurb: typeMeta?.blurb?.trim() || tabMeta.blurb,
    icon: tabMeta.icon,
  };
  // Personality/IELTS pre-fill one fixed programme - editable via the option list.
  const autoProg =
    (typeof typeMeta?.options?.autoProgramme === "string" && typeMeta.options.autoProgramme) ||
    autoProgramme[tab];
  // Batch choices come from what's ticked on the chosen course (Weekdays / Weekends); none ticked = no batch field.
  const batchCourse = tab === "travel" || tab === "it" ? (values.programme ?? "") : (autoProg ?? "");
  const courseBatches = batchCourse ? typeMeta?.options?.[`batches:${batchCourse}`] : undefined;
  const batchChoices = Array.isArray(courseBatches) ? courseBatches : listOpt(typeMeta, "batchOptions", batchOptions);
  const hasBatch =
    (tab === "travel" || tab === "personality" || tab === "it" || tab === "ielts") && !!batchCourse && batchChoices.length > 0;
  const selectedBatch = batchChoices.includes(values.batch ?? "") ? values.batch! : pickDefaultBatch(typeMeta, batchChoices);
  const isCorporate = tab === "corporate";
  const interested = values.interestedCourse ?? "";

  function set(key: string, value: string) {
    const resetProgramme = key === "interestedCourse";
    if (resetProgramme) setProgrammes([]);
    setValues((p) => ({ ...p, [key]: value, ...(resetProgramme ? { programme: "" } : null) }));
    setErrors((p) => ({ ...p, [key]: "", ...(resetProgramme ? { programme: "" } : null) }));
  }

  function toggleProgramme(option: string) {
    setProgrammes((p) => (p.includes(option) ? p.filter((x) => x !== option) : [...p, option]));
    setErrors((p) => ({ ...p, programme: "" }));
  }

  function pickTab(next: EnquiryFormTab) {
    setTab(next);
    setErrors({});
    setFormError("");
    // programme options differ per tab - never carry a stale pick over
    setValues((p) => {
      const n = { ...p };
      delete n.programme;
      return n;
    });
    setProgrammes([]);
  }

  /** Sub-course picker shown inside the General Enquiry form once a category is chosen. */
  const generalSubOptions = useMemo(() => {
    // any course category with courses gets a course picker (live catalogue), else the static lists
    const live = typeMeta?.options?.[`courses:${interested}`];
    if (Array.isArray(live) && live.length > 0) return live;
    if (interested === "Travel & Tourism") return travelCourseOptions;
    if (interested === "IT Courses") return itCourseOptions;
    return null;
  }, [interested, typeMeta]);
  // The Contact page's "General" mode leaves category + course optional; the "Enquiry" mode (all tabs) requires them.
  const courseOptional = scope === "general";
  // every category's course picker is multi-select now
  const generalMulti = interested === "Corporate Training" || !!generalSubOptions;
  const multiProgrammes = isCorporate || (tab === "general" && generalMulti);
  const otherProgramme = multiProgrammes && programmes.some(isOther);
  const otherMode = isCorporate && isOther(values.mode ?? "");

  const requiredKeys = useMemo(() => {
    if (isCorporate)
      return ["company", "name", "email", "phone", "location", "requirement"];
    return ["name", "phone", "email", "city", tab === "general" && !courseOptional ? "interestedCourse" : "", "message"].filter(
      Boolean
    );
  }, [isCorporate, tab, courseOptional]);

  function validate(): boolean {
    const e: Record<string, string> = {};
    for (const key of requiredKeys) {
      const label = LABELS[key] ?? key;
      const v = (values[key] ?? "").trim();
      if (!v) e[key] = `${label} is required`;
      else if (key === "email" && !emailRe.test(v)) e[key] = "Enter a valid email address";
      else if (key === "email" && blockedEmailError(v)) e[key] = blockedEmailError(v);
      else if (key === "phone" && validatePhone(v)) e[key] = validatePhone(v);
      else if (key === "city" && !cityRe.test(v)) e[key] = "City should contain letters only";
    }
    const needsProgramme =
      isCorporate ||
      tab === "travel" ||
      tab === "it" ||
      (tab === "general" &&
        !courseOptional &&
        (interested === "Travel & Tourism" || interested === "IT Courses" || generalMulti));
    if (needsProgramme) {
      if (isCorporate || generalMulti) {
        if (programmes.length === 0) e.programme = "Select at least one training programme";
      } else if (!(values.programme ?? "").trim()) {
        e.programme = "Please choose a training programme";
      }
    }
    if (otherProgramme && !(values.programmeOther ?? "").trim()) e.programmeOther = "Please tell us which programme you need";
    if (otherMode && !(values.modeOther ?? "").trim()) e.modeOther = "Please tell us the training mode you prefer";
    if (!values.consent) e.consent = "Please accept the privacy notice to continue";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (isStaffSession()) {
      toast.info(STAFF_ENQUIRY_NOTICE.title, STAFF_ENQUIRY_NOTICE.message);
      return;
    }
    if (!validate()) return;
    setStatus("loading");
    setFormError("");
    try {
      // a student who is already in a chosen course cannot enquire about it again
      const session = readSession();
      if (session?.role === "student") {
        const chosen = [values.programme, ...programmes].filter(Boolean).map((p) => String(p).trim().toLowerCase());
        const mine = chosen.length ? await apiMyCourses(session.token).catch(() => []) : [];
        const dup = mine.find((c) => chosen.includes(c.course.title.trim().toLowerCase()));
        if (dup) {
          setStatus("idle");
          toast.info("Already enrolled", `You are already enrolled in ${dup.course.title}.`);
          return;
        }
      }
      // the typed details travel with the "Other" choice, e.g. "Other: Leadership coaching"
      const { programmeOther, modeOther, batch: _batch, ...fields } = values;
      const payload = {
        ...fields,
        ...(hasBatch && selectedBatch ? { batch: selectedBatch } : {}),
        ...(otherMode ? { mode: `Other: ${(modeOther ?? "").trim()}` } : {}),
      };
      const sentProgrammes = programmes.map((p) => (isOther(p) && otherProgramme ? `Other: ${(programmeOther ?? "").trim()}` : p));
      const ref = await submitEnquiryApi({ kind: tab, ...payload, programmes: sentProgrammes, ...(courseOptional ? { courseOptional: true } : {}) });
      setReference(ref);
      setStatus("done");
    } catch (e) {
      setStatus("idle");
      setFormError(e instanceof Error ? e.message : "Could not submit your enquiry. Please try again.");
    }
  }

  return (
    <div>
      {/* enquiry type dropdown (hidden for now)
      {scope === "all" && (
        <div className="mb-5">
          <label htmlFor="enquiry-type" className="mb-1.5 block text-sm font-medium text-ink">
            Enquiry type
          </label>
          <select
            id="enquiry-type"
            value={tab}
            onChange={(e) => pickTab(e.target.value as EnquiryFormTab)}
            className={cn(base(), "h-11 max-w-sm")}
          >
            {TABS.map((t) => (
              <option key={t.id} value={t.id}>
                {types?.find((x) => x.key === t.id)?.label ?? t.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ink-muted">{active.blurb}</p>
        </div>
      )}
      */}

      {/* form card */}
      <div className="mt-6 overflow-hidden rounded-[var(--radius-card)] border border-line bg-white shadow-lift">
        {/* the Enquiry tab goes straight to the form - the title/blurb header is only for the single-form (General) view */}
        {scope !== "all" && (
          <div className="border-b border-line bg-cream/60 px-6 py-5 sm:px-8">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-600 text-white">
                {active.icon}
              </span>
              <div>
                <h3 className="font-display text-lg font-semibold leading-tight text-ink">{active.label}</h3>
                <p className="mt-0.5 text-[13px] leading-snug text-ink-soft">{active.blurb}</p>
              </div>
            </div>
          </div>
        )}

        <div className="p-6 sm:p-8">
          {status === "done" ? (
            <SuccessNote
              corporate={isCorporate}
              reference={reference}
              onReset={() => {
                setStatus("idle");
                setValues({});
                setProgrammes([]);
                setFormError("");
              }}
            />
          ) : (
            <form onSubmit={onSubmit} noValidate className="min-w-0 space-y-5">
              {/* your details */}
              <fieldset className="min-w-0 space-y-4">
                <Legend>Your details</Legend>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                  {isCorporate && (
                    <TextInput label="Company name" name="company" required value={values.company ?? ""} error={errors.company} onChange={set} />
                  )}
                  <TextInput
                    label={isCorporate ? "Your name" : "Name"}
                    name="name"
                    required
                    autoComplete="name"
                    value={values.name ?? ""}
                    error={errors.name}
                    onChange={(k, v) => set(k, cleanCity(v))}
                  />
                  {isCorporate && (
                    <TextInput label="Designation" name="designation" value={values.designation ?? ""} onChange={(k, v) => set(k, cleanCity(v))} />
                  )}
                  <Wrapper name="phone" label="Mobile number" required error={errors.phone}>
                    <PhoneInput id="w-phone" name="phone" value={values.phone ?? ""} error={errors.phone} onChange={(v) => set("phone", v)} />
                  </Wrapper>
                  <TextInput
                    label="Email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={values.email ?? ""}
                    error={errors.email}
                    onChange={set}
                  />
                  {isCorporate ? (
                    <TextInput label="Location" name="location" required value={values.location ?? ""} error={errors.location} onChange={(k, v) => set(k, cleanCity(v))} />
                  ) : (
                    <TextInput label="City" name="city" required value={values.city ?? ""} error={errors.city} onChange={(k, v) => set(k, cleanCity(v))} />
                  )}
                </div>
              </fieldset>

              {/* programme */}
              <fieldset className="min-w-0 space-y-4">
                <Legend>Programme</Legend>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                  {tab === "general" && (
                    <SelectInput
                      label="Course Category"
                      name="interestedCourse"
                      required={!courseOptional}
                      value={interested}
                      error={errors.interestedCourse}
                      onChange={set}
                      options={listOpt(typeMeta, "interestedCourses", interestedCourses)}
                      placeholder="Select a category"
                      className="sm:col-span-2"
                    />
                  )}

                  {(tab === "travel" || tab === "it") && (
                    <SelectInput
                      label="Preferred training programme"
                      name="programme"
                      required
                      value={values.programme ?? ""}
                      error={errors.programme}
                      onChange={set}
                      options={withCurrent(listOpt(typeMeta, "programmes", tab === "travel" ? travelProgrammes : itCourseOptions), values.programme)}
                      placeholder="Select a programme"
                      className="sm:col-span-2"
                    />
                  )}

                  {autoProg && (
                    <div className="sm:col-span-2">
                      <Label label="Preferred training programme" required />
                      <input
                        readOnly
                        value={autoProg}
                        aria-label="Preferred training programme"
                        className="h-11 w-full cursor-default rounded-xl border border-line bg-cream/70 px-3.5 text-sm font-medium text-brand-700 outline-none"
                      />
                    </div>
                  )}

                  {isCorporate && (
                    <MultiSelect
                      label="Preferred training programme"
                      options={listOpt(typeMeta, "programmes", corporateProgrammes)}
                      selected={programmes}
                      onToggle={toggleProgramme}
                      error={errors.programme}
                    />
                  )}

                  {tab === "general" && generalMulti && (
                    <MultiSelect
                      label="Course"
                      options={generalSubOptions ?? listOpt(typeMeta, "corporateProgrammes", corporateProgrammes)}
                      selected={programmes}
                      onToggle={toggleProgramme}
                      error={errors.programme}
                    />
                  )}

                  {otherProgramme && (
                    <TextInput
                      label="Other programme details"
                      name="programmeOther"
                      required
                      value={values.programmeOther ?? ""}
                      error={errors.programmeOther}
                      onChange={set}
                      placeholder="Tell us the programme or topic you need"
                      className="sm:col-span-2"
                    />
                  )}

                  {isCorporate && (
                    <SelectInput
                      label="Training mode"
                      name="mode"
                      value={values.mode ?? ""}
                      onChange={set}
                      options={listOpt(typeMeta, "trainingModes", trainingModes)}
                      placeholder="Select a mode"
                    />
                  )}

                  {otherMode && (
                    <TextInput
                      label="Other training mode details"
                      name="modeOther"
                      required
                      value={values.modeOther ?? ""}
                      error={errors.modeOther}
                      onChange={set}
                      placeholder="How would you like the training delivered?"
                    />
                  )}

                  {hasBatch && (
                    <SelectInput
                      label="Preferred batch"
                      name="batch"
                      value={selectedBatch}
                      onChange={set}
                      disabled={batchChoices.length === 1}
                      options={batchChoices}
                      placeholder="Select a batch"
                    />
                  )}
                </div>
              </fieldset>

              {/* message */}
              <fieldset className="min-w-0 space-y-4">
                <Legend>{isCorporate ? "Requirement" : "Anything else?"}</Legend>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                  {isCorporate ? (
                    <TextArea
                      label="Training requirement"
                      name="requirement"
                      required
                      rows={4}
                      placeholder="Team size, sessions needed, dates…"
                      value={values.requirement ?? ""}
                      error={errors.requirement}
                      onChange={set}
                    />
                  ) : (
                    <TextArea
                      label="Message"
                      name="message"
                      required
                      rows={4}
                      placeholder="Tell us a little about your goals…"
                      value={values.message ?? ""}
                      error={errors.message}
                      onChange={set}
                    />
                  )}
                </div>
              </fieldset>

              <div>
                <label className="flex items-start gap-3 text-sm text-ink-soft">
                  <input
                    type="checkbox"
                    checked={!!values.consent}
                    onChange={(e) => set("consent", e.target.checked ? "yes" : "")}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-line accent-brand-600 focus:ring-brand-600/30"
                  />
                  <span>
                    I have read and agree to the{" "}
                    <LegalModalLink doc="terms" className="font-medium text-brand-700 underline-offset-2 hover:underline">
                      terms and conditions
                    </LegalModalLink>{" "}
                    laid by My Value Academy. See our{" "}
                    <LegalModalLink doc="privacy" className="font-medium text-brand-700 underline-offset-2 hover:underline">
                      privacy notice
                    </LegalModalLink>
                    .
                  </span>
                </label>
                {errors.consent && <p className="mt-1 text-xs text-brand-600">{errors.consent}</p>}
              </div>

              {formError && (
                <p
                  role="alert"
                  className="rounded-xl border border-brand-500/30 bg-brand-600/5 px-3.5 py-2.5 text-sm text-brand-700"
                >
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-[var(--radius-btn)] bg-brand-600 px-6 text-[15px] font-medium text-white shadow-soft transition-all hover:bg-brand-700 hover:shadow-lift disabled:opacity-70"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" /> Submit enquiry
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Legend({ children }: { children: ReactNode }) {
  return (
    <legend className="flex w-full items-center gap-3 text-[11px] font-bold uppercase tracking-[0.14em] text-ink-muted">
      {children}
      <span className="h-px flex-1 bg-line" />
    </legend>
  );
}

function SuccessNote({
  corporate,
  reference,
  onReset,
}: {
  corporate: boolean;
  reference: string;
  onReset: () => void;
}) {
  return (
    <div className="flex flex-col items-center py-8 text-center">
      <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-600/10">
        <CheckCircle2 className="h-9 w-9 text-brand-600" />
      </span>
      <h3 className="mt-4 font-display text-xl font-semibold text-ink">Enquiry received</h3>
      <p className="mt-2 max-w-sm text-sm text-ink-soft">
        {corporate
          ? "Our corporate training team will reach out to discuss your requirement."
          : "An admissions advisor will contact you shortly to help you choose a batch."}
      </p>
      <button
        type="button"
        onClick={onReset}
        className="mt-5 rounded-lg border border-line bg-white px-4 py-2 text-sm font-medium text-ink-soft transition-colors hover:border-brand-400 hover:text-brand-700"
      >
        Send another
      </button>
    </div>
  );
}

const LABELS: Record<string, string> = {
  company: "Company name",
  name: "Name",
  phone: "Mobile number",
  email: "Email",
  city: "City",
  location: "Location",
  requirement: "Training requirement",
  interestedCourse: "Course category",
  message: "Message",
};

/* ------------------------------ inputs ------------------------------ */

const base = (error?: string) =>
  cn(
    "w-full rounded-xl border bg-white px-3.5 text-sm text-ink outline-none transition placeholder:text-ink-muted/70 focus:ring-2",
    error
      ? "border-brand-500 focus:border-brand-500 focus:ring-brand-600/20"
      : "border-line focus:border-brand-500 focus:ring-brand-600/15"
  );

function Label({ label, required }: { label: string; required?: boolean }) {
  return (
    <span className="mb-1.5 block text-sm font-medium text-ink">
      {label}
      {required && <span className="text-brand-600"> *</span>}
    </span>
  );
}

function Wrapper({
  name,
  label,
  required,
  error,
  className,
  children,
}: {
  name: string;
  label: string;
  required?: boolean;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <label htmlFor={`w-${name}`} className="mb-1.5 block text-sm font-medium text-ink">
        {label}
        {required && <span className="text-brand-600"> *</span>}
      </label>
      {children}
      {error && (
        <p id={`w-${name}-err`} className="mt-1 text-xs text-brand-600">
          {error}
        </p>
      )}
    </div>
  );
}

// Mirrors the server-side caps so visitors cannot type past what the API accepts.
const MAX_LEN: Record<string, number> = {
  name: 120, email: 190, city: 100, company: 150, designation: 100, location: 150, message: 2000, requirement: 3000, programmeOther: 150, modeOther: 150,
};

function TextInput({
  label,
  name,
  value,
  error,
  onChange,
  type = "text",
  required,
  placeholder,
  autoComplete,
  className,
}: {
  label: string;
  name: string;
  value: string;
  error?: string;
  onChange: (k: string, v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
  className?: string;
}) {
  return (
    <Wrapper name={name} label={label} required={required} error={error} className={className}>
      <input
        id={`w-${name}`}
        name={name}
        type={type}
        value={value}
        maxLength={MAX_LEN[name]}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(name, e.target.value)}
        aria-invalid={!!error}
        className={cn(base(error), "h-11")}
      />
    </Wrapper>
  );
}

function TextArea({
  label,
  name,
  value,
  error,
  onChange,
  required,
  rows = 4,
  placeholder,
}: {
  label: string;
  name: string;
  value: string;
  error?: string;
  onChange: (k: string, v: string) => void;
  required?: boolean;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <Wrapper name={name} label={label} required={required} error={error} className="sm:col-span-2">
      <textarea
        id={`w-${name}`}
        name={name}
        rows={rows}
        value={value}
        maxLength={MAX_LEN[name]}
        placeholder={placeholder}
        onChange={(e) => onChange(name, e.target.value)}
        aria-invalid={!!error}
        className={cn(base(error), "resize-y py-2.5")}
      />
    </Wrapper>
  );
}

/** The preselected course must be selectable even when the option list does not include it. */
function withCurrent(options: string[], current?: string) {
  return current && !options.includes(current) ? [...options, current] : options;
}

function SelectInput({
  label,
  name,
  value,
  onChange,
  options,
  placeholder,
  required,
  error,
  className,
  disabled,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (k: string, v: string) => void;
  options: string[];
  placeholder?: string;
  required?: boolean;
  error?: string;
  className?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const choices = [{ v: "", l: placeholder ?? "Select an option" }, ...options.map((o) => ({ v: o, l: o }))];

  return (
    <Wrapper name={name} label={label} required={required} error={error} className={className}>
      <div ref={wrap} className="relative">
        <button
          type="button"
          id={`w-${name}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-invalid={!!error}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className={cn(base(error), "flex h-11 items-center justify-between gap-2 text-left", !value && "text-ink-muted", disabled && "cursor-not-allowed opacity-70")}
        >
          <span className="truncate">{value || placeholder || "Select an option"}</span>
          <ChevronDown className={cn("h-4 w-4 shrink-0 text-ink-muted transition-transform", open && "rotate-180")} />
        </button>
        {open && (
          <div role="listbox" className="absolute left-0 right-0 z-20 mt-1 max-h-64 overflow-y-auto rounded-xl border border-line bg-cream p-1.5 shadow-lift [scrollbar-color:var(--color-brand-400)_var(--color-cream)] [scrollbar-width:thin]">
            {choices.map((c) => {
              const on = c.v === value;
              return (
                <button
                  key={c.v || "__none"}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => {
                    onChange(name, c.v);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-cream",
                    on ? "bg-brand-600/8 font-semibold text-brand-700" : c.v ? "text-ink" : "text-ink-muted"
                  )}
                >
                  {c.l}
                  {on && c.v && <Check className="h-4 w-4 shrink-0" strokeWidth={2.5} />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Wrapper>
  );
}

function MultiSelect({
  label,
  options,
  selected,
  onToggle,
  error,
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
  error?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  return (
    <div ref={wrap} className="relative sm:col-span-2">
      <Label label={label} required />
      <p className="-mt-1 mb-2 text-xs text-ink-muted">Select all that apply.</p>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex min-h-11 w-full items-center justify-between gap-2 rounded-xl border bg-white px-3.5 py-1.5 text-left text-sm outline-none transition-colors focus:border-brand-600",
          error ? "border-brand-600" : "border-line"
        )}
      >
        <span className="flex flex-wrap gap-1">
          {selected.length === 0 && <span className="text-ink-muted">Select programmes</span>}
          {selected.map((o) => (
            <span key={o} className="rounded-full bg-brand-600/10 px-2 py-0.5 text-[12px] font-semibold text-brand-700">
              {o}
            </span>
          ))}
        </span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-ink-muted transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div role="listbox" aria-multiselectable="true" className="absolute left-0 right-0 z-20 mt-1 max-h-64 overflow-y-auto rounded-xl border border-line bg-white p-1.5 shadow-lift">
          {options.map((o) => {
            const on = selected.includes(o);
            return (
              <button
                key={o}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => onToggle(o)}
                className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-ink hover:bg-cream"
              >
                <span
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                    on ? "border-brand-600 bg-brand-600 text-white" : "border-line"
                  )}
                >
                  {on && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
                {o}
              </button>
            );
          })}
        </div>
      )}
      {error && <p className="mt-1 text-xs text-brand-600">{error}</p>}
    </div>
  );
}
